/**
 * Shared contracts for the eight bespoke planning components — design/09 §8.
 *
 * These types are the interface eight chapter agents build against, so they are
 * specified fully here rather than grown per screen. Where a type looks
 * over-specified, it is encoding a rule:
 *
 *   `MeasureDefinition.polarity`   design/09 §5.3 — never hard-code green-means-up
 *   `CellValue` missing states     reqs/00 D8 — not-ranged ≠ not-reported ≠ counted zero
 *   `Lineage`                      G2.2 — "every number answers 'where did this come from'"
 *   `ScenarioContext.baseline*`    §8 — a commit names its scenario AND its baseline
 *   `ReasonCode`                   §8 — an AI recommendation shows its reason codes
 *                                  BEFORE it can be accepted
 *   `FeedCertification`            reqs/08 §2 — planners must see when a plan is
 *                                  running on a quarantined feed
 */

import type { ICUValues } from '../primitives';
import type { Polarity } from '../export/types';
import type { DensityMode } from '../tokens/generated/tokens';

export type { DensityMode };
export type { Polarity };
export type { ICUValues };

import type { GridRow } from './planning-grid/PlanningGrid';

// ── values and their provenance ──────────────────────────────────────────────

/** reqs/00 D8 — three distinguishable kinds of nothing, plus a real number. */
export type CellValue =
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'text'; readonly value: string }
  | { readonly kind: 'counted-zero' }
  | { readonly kind: 'not-ranged' }
  | { readonly kind: 'not-reported' };

export const isNumeric = (v: CellValue): v is { kind: 'number'; value: number } => v.kind === 'number';

/** A counted zero IS a number (reqs/00 D9); every other absence collapses to `null`. */
export const toNumberOrNull = (v: CellValue): number | null =>
  isNumeric(v) ? v.value : v.kind === 'counted-zero' ? 0 : null;

/**
 * G2.2 — "Every number answers 'where did this come from' — a lineage affordance
 * showing the measure definition (from the reqs/00 D9 semantic layer), the source
 * batch, and the plan version."
 *
 * Required on every editable and every aggregate cell. Not optional, because a cell
 * whose lineage is unknown is a cell whose number cannot be defended in a review.
 */
export interface Lineage {
  readonly measure: string;
  /** Verbatim from the semantic layer. Never re-stated in the UI. */
  readonly definition: string;
  readonly registerVersion: string;
  readonly sourceBatch: string;
  readonly planVersionId: string;
  /** Drill target for this specific number, if it is an aggregate. */
  readonly drillTo?: string;
}

export interface MeasureDefinition {
  readonly key: string;
  readonly labelKey: string;
  /** Shown beside the column header, not repeated per cell (design/09 §4.3). */
  readonly unitKey: string;
  /** design/09 §5.3 — declared per measure, read by the UI. Never assumed. */
  readonly polarity: Polarity;
  readonly currency?: string;
  readonly editable: boolean;
  readonly registerVersion: string;
  /**
   * What the column ORDERS by, where the shown string is not its order (a `PlanningGrid`
   * column; `docs/adr/6000` amendment 2026-09-30 (c)). A day shown as «28 set 2026» sorts by its ISO
   * timestamp, a status word by its place in the status order, a dash by nothing.
   *
   * `null` is "no value": it sorts LAST in both directions, like an absent cell. Omitted, the
   * column orders by its cell (a number by its value, a text by its string). Only the order
   * uses it: the cell, the CSV and the `?sort=` key are unchanged.
   */
  readonly sortValue?: (row: GridRow) => number | string | null;
}

// ── hierarchy ────────────────────────────────────────────────────────────────

export interface HierarchyNode {
  readonly id: string;
  /** Reference data, not a catalogue key — a category name is data (G6). */
  readonly label: string;
  readonly level: string;
  readonly childCount: number;
  readonly children?: readonly HierarchyNode[];
}

export interface HierarchySelection {
  readonly nodeIds: readonly string[];
  /** Whether selecting a parent implies its descendants. */
  readonly mode: 'explicit' | 'cascade';
}

// ── scenario and baseline ────────────────────────────────────────────────────

/**
 * design/09 §8: the scenario switcher is "persistent, always visible, showing scenario
 * name and baseline version — reqs/07 blocks commits without both".
 */
export interface ScenarioContext {
  readonly scenarioId: string;
  readonly scenarioName: string;
  readonly baselineVersionId: string;
  readonly baselineVersionLabel: string;
  readonly status: 'draft' | 'in-review' | 'approved' | 'locked';
  /** UTC ISO-8601. */
  readonly lastRecomputedAt: string;
}

export type CommitBlockReason = 'no-baseline' | 'quarantined-feed' | 'stale-recompute' | 'insufficient-permission';

// ── edits ────────────────────────────────────────────────────────────────────

export interface CellEdit {
  readonly rowId: string;
  readonly columnKey: string;
  readonly previous: CellValue;
  readonly next: CellValue;
  /** Bulk operations carry a reason code, previewed before commit (G2.3). */
  readonly reasonCode?: string;
}

export interface EditState {
  readonly dirty: readonly CellEdit[];
  /** G2 §2.6 — undo for every destructive or bulk action, window in minutes. */
  readonly undoStack: readonly CellEdit[][];
  readonly redoStack: readonly CellEdit[][];
}

// ── exceptions ───────────────────────────────────────────────────────────────

export const THRESHOLD_CLASSES = ['good', 'warning', 'serious', 'critical'] as const;
export type ThresholdClass = (typeof THRESHOLD_CLASSES)[number];

export interface Exception {
  readonly id: string;
  readonly thresholdClass: ThresholdClass;
  /** ICU key for the exception type — "size run broken", "cover below target". */
  readonly typeKey: string;
  /**
   * ICU arguments for `typeKey`.
   *
   * A queue reads its rows from `/foundations/exceptions`, whose `rule_id` is an
   * unconstrained string: the platform can register a rule this build has no label for at
   * any time. Without arguments such a row could only be rendered as a generic
   * "unclassified", which is what ch00 and ch04 both had to do — the rule identifier, the
   * one datum that makes the row actionable, did not reach the screen at all.
   *
   * With them the catalogue can say "rule {rule}" and the translator decides where
   * `{rule}` sits in the sentence. Never a concatenation (G6 test b), and never a label
   * guessed from the identifier: an identifier rendered as itself is a datum, and a
   * plausible wrong name for it is a lie about which rule fired.
   */
  readonly typeValues?: ICUValues;
  readonly entityId: string;
  readonly entityLabel: string;
  readonly measure: string;
  readonly actual: CellValue;
  readonly expected: CellValue;
  readonly detectedAt: string;
  readonly lineage: Lineage;
  /** Actions available on this exception, for bulk selection. */
  readonly actions: readonly string[];
}

// ── recommendations ──────────────────────────────────────────────────────────

/**
 * design/09 §8: "Every AI recommendation shows its reason codes before it can be
 * accepted." The type makes `reasonCodes` non-optional and non-empty by convention,
 * and `RecommendationCard` refuses to render an accept action without them.
 */
export interface ReasonCode {
  readonly code: string;
  readonly labelKey: string;
  /** ICU arguments for `labelKey` — e.g. the raw code of a driver this build does not name. */
  readonly labelValues?: ICUValues;
  /** Contribution to the recommendation, signed. Shown, not hidden behind a score. */
  readonly contribution: number;
}

export interface Recommendation {
  readonly id: string;
  readonly titleKey: string;
  readonly entityLabel: string;
  readonly proposed: CellValue;
  readonly current: CellValue;
  /**
   * What the two figures are called. Default "Current" and "Proposed"; a replenishment line
   * compares what the doors REQUESTED with what the engine PROPOSED (bata-ui#148 3.3).
   */
  readonly currentLabelKey?: string;
  readonly proposedLabelKey?: string;
  readonly measure: MeasureDefinition;
  readonly reasonCodes: readonly ReasonCode[];
  /**
   * Why `reasonCodes` is empty, when the producer can say — e.g. "the platform version that
   * wrote this run publishes no decomposition". Rendered instead of the generic "carries no
   * reason codes"; the recommendation stays unacceptable either way.
   */
  readonly reasonsAbsentKey?: string;
  /**
   * What the list of `reasonCodes` is CALLED on this card, counted — default «4 reason codes».
   *
   * An app whose row carries a field of its own named "reason code" must override it. A
   * replenishment proposal does: `reason_code` is a published column saying why the door asked,
   * drawn as a badge, and `reasonCodes` here holds something else entirely — the signed driver
   * decomposition (platform ADR 6038). Both were called "reason code" within three centimetres of
   * each other on the proposal drawer, which is a naming collision a planner has no way to
   * resolve from the screen.
   */
  readonly reasonsSummaryKey?: string;
  /** The unit the contributions are in, e.g. pairs. Absent means the measure's own unit. */
  readonly contributionUnitKey?: string;
  /**
   * `null` where the producing model publishes none (reqs/00 D9: undefined is not zero —
   * a card showing 0% confidence on a model that reports none is a warning nobody issued).
   */
  readonly confidence: number | null;
  /** `null` where the producer names no model version. Never an empty string. */
  readonly modelVersion: string | null;
  readonly lineage: Lineage;
}

export type RecommendationDecision = 'accept' | 'adjust' | 'reject';

// ── size runs ────────────────────────────────────────────────────────────────

export interface SizeRun {
  /** Sizes in SCALE order, declared. §12.11 — never lexicographic. */
  readonly sizes: readonly string[];
  /** Aligned to `sizes`. A size never ranged is not a size that sold nothing. */
  readonly availability: readonly CellValue[];
  /** The sizes that carry the majority of demand — a break here is material. */
  readonly coreSizes: readonly string[];
}

// ── feeds ────────────────────────────────────────────────────────────────────

export const FEED_STATES = ['certified', 'degraded', 'quarantined', 'awaiting'] as const;
export type FeedState = (typeof FEED_STATES)[number];

/** reqs/08 §2 — certification state per feed. */
export interface FeedCertification {
  readonly feedId: string;
  readonly feedLabel: string;
  readonly state: FeedState;
  readonly lastLoadedAt: string;
  readonly checkedAt: string;
  /** ICU key naming the failing certification test, when not certified. */
  readonly failureKey?: string;
}

// ── variance bridge ──────────────────────────────────────────────────────────

/**
 * design/09 §8: "Waterfall from last-approved to proposed, ADDITIVE WITH ZERO
 * RESIDUAL (reqs/04 §4.3)." The zero-residual property is asserted at runtime by
 * `assertBridgeBalances` — a bridge whose components do not sum to its total is a
 * bridge that is telling the planner a story rather than an arithmetic.
 */
export interface BridgeStep {
  readonly id: string;
  readonly labelKey: string;
  readonly delta: number;
  readonly lineage: Lineage;
}

export interface VarianceBridge {
  readonly fromLabelKey: string;
  readonly fromValue: number;
  readonly toLabelKey: string;
  readonly toValue: number;
  readonly steps: readonly BridgeStep[];
  readonly unitKey: string;
  readonly polarity: Polarity;
}

export class BridgeResidualError extends Error {
  constructor(residual: number, expected: number, actual: number) {
    super(
      `Variance bridge does not balance: steps sum to ${actual} but the movement from `
      + `baseline to proposed is ${expected}, leaving a residual of ${residual}. `
      + 'design/09 §8 requires the bridge to be additive with ZERO residual (reqs/04 §4.3). '
      + 'A residual means at least one driver is unattributed, and a planner reading the '
      + 'bridge would conclude the movement is explained when it is not.',
    );
    this.name = 'BridgeResidualError';
  }
}

/** Floating-point tolerance: units are integers, currency has at most two decimals. */
const RESIDUAL_TOLERANCE = 1e-6;

export function assertBridgeBalances(bridge: VarianceBridge): void {
  const expected = bridge.toValue - bridge.fromValue;
  const actual = bridge.steps.reduce((sum, s) => sum + s.delta, 0);
  const residual = expected - actual;
  if (Math.abs(residual) > RESIDUAL_TOLERANCE) {
    throw new BridgeResidualError(residual, expected, actual);
  }
}
